import { useCallback, useEffect, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';
import moment from 'moment';

import {
  AddressNoteApi,
  CautionApi,
  FlagsApi,
  MasterIndexApi,
  MasterNameApi,
} from '@/api';
import { MasterIndexApiPath } from '@/api/api.master.index';
import { MasterVehicleApi } from '@/api/api.vehicles.master';
import { DATE_FORMAT } from '@/config';
import { FlagOrigin, IPersonFlag } from '@/models/flag';
import { INote } from '@/models/note';
import { MasterIndexType } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';
import { getRelatedRecordPath } from '@/pages/MasterIndex/components/RelatedRecordsCols';
import { getAddressLocation } from '@/services';
import { getFormFields } from '@/services/service.formfieldLoader';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { formatPersonName } from '@/utils';

import {
  AnyMasterRecord,
  MergeConflict,
  getConflicts,
  getNestedValue,
  setNestedValue,
} from './mergeFieldConfig';

/**
 * Pre-plan subfield meta keys never resolved through the merge UI.
 *
 * `agencyId` / `prePlanOption` are scope identifiers (filled server-side
 * from the request agency, not user-editable). `submittedBy` /
 * `dateSubmitted` are audit metadata: surfacing them as conflict rows
 * would let the user "merge" who-submitted-what, which doesn't match the
 * regular flow's semantics (BE auto-sets these on save). `_id` is
 * irrelevant — pre-plan entries are identified by `(agencyId,
 * prePlanOption)`, not by their own id.
 */
const PREPLAN_META_KEYS = new Set([
  'agencyId',
  'prePlanOption',
  'submittedBy',
  'dateSubmitted',
  '_id',
]);

const PREPLAN_CONFLICT_PREFIX = '__prePlan__';

/**
 * Pull the user-agency entry out of an address's `addressPrePlans` array.
 *
 * The BE filters the GET response to current-agency entries already, but
 * we still match by `agencyId` defensively so an older BE that doesn't
 * filter still produces the right entry on the FE.
 */
function pickCurrentAgencyPrePlan(
  record: unknown,
  userAgencyId: string | null,
): Record<string, unknown> | null {
  if (!userAgencyId) return null;
  const plans = (record as any)?.addressPrePlans;
  if (!Array.isArray(plans)) return null;
  const found = plans.find(
    (p: any) => p && String(p.agencyId) === userAgencyId,
  );
  return found ?? null;
}

function isValueEmpty(v: unknown): boolean {
  if (v == null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

type PrePlanFormField = {
  name: string;
  label: string;
  type?: string;
  items?: unknown;
};

/**
 * Display formatter for a pre-plan subfield, matching the read-only
 * preview in AddressPreplanModal: dates run through `DATE_FORMAT`,
 * booleans become Yes/No, select values map to their option label.
 * Returns `null` to defer to MergeConflictStep's default formatting
 * (handles strings/numbers gracefully).
 */
function makePrePlanFormatter(
  field: PrePlanFormField,
): (value: unknown) => string | null {
  return (value) => {
    if (value == null || value === '') return null;
    if (field.type === 'date') {
      return value ? moment(value as string).format(DATE_FORMAT) : null;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (field.type === 'select' && Array.isArray(field.items)) {
      const found = (
        field.items as Array<{ value: unknown; label: string }>
      ).find((it) => it.value === value);
      return found?.label ?? null;
    }
    if (field.type === 'select' && field.items === 'getAgencyEmployees') {
      const u = value as { firstName?: string; lastName?: string } | null;
      const name = formatPersonName(u);
      return name || 'N/A';
    }
    return null;
  };
}

type FieldChoice = 'current' | 'selected';

export type MergeRelatedRecord = {
  _id: string;
  /** Normalized type label shown in the chip. e.g. 'CFS', 'CASE', 'TASK'. */
  type: string;
  /** Natural id as shown in the master-index detail page (CFS number, case number, etc.). Falls back to `_id`. */
  displayId?: string;
  /** Optional URL to open in a new tab. */
  path?: string;
  /** Optional location string (already-formatted). */
  location?: string;
};

// Mirrors of the backend per-type writable-field whitelists. The merge POST
// payload's `mergedFields` is filtered through these — fields outside the set
// cause the backend to reject the request with `unknown field: <key>`. Keep in
// sync with account_be/src/services/v1/master.merge/merge.{type}.ts.
const WRITABLE_FIELDS_BY_TYPE: Record<MasterIndexType, Set<string>> = {
  persons: new Set([
    'suffix',
    'firstName',
    'middleName',
    'lastName',
    'maidenName',
    'aliasName',
    'nickName',
    'birthday',
    'placeOfBirth',
    'sex',
    'race',
    'religion',
    'education',
    'ssn',
    'nysid',
    'fbiNumber',
    'nationality',
    'citizenOf',
    'ethnicOrigin',
    'refNumber',
    'transient',
    'maritalStatus',
    'spokenLanguages',
    'socialMedia',
    'gangAffiliations',
    'addresses',
    'phoneNumbers',
    'emails',
    'emergencyContacts',
    'physicalCharacteristics',
    'address',
    'employerInfo',
    'driverLicense',
    'isVerified',
    'files',
  ]),
  organizations: new Set([
    'name',
    'organizationType',
    'notes',
    'nationality',
    'gangAffiliations',
    'phoneNumbers',
    'emails',
    'addresses',
    'contacts',
    'urls',
    'socialMediaLinks',
    'emergencyContacts',
    'socialMedia',
    'isVerified',
    'address',
    'files',
  ]),
  vehicles: new Set([
    'type',
    'make',
    'model',
    'year',
    'color',
    'style',
    'vin',
    'licensePlates',
    'owner',
    'uniqueCharacteristics',
    'isVerified',
    'files',
  ]),
  address: new Set([
    'addressLabel',
    'addressNumber',
    'subAddress',
    'completeStreetName',
    'state',
    'zipcode',
    'zipName',
    'country',
    'placeName',
    'normalized',
    'point',
    'pointType',
    'intersectionStreets',
    'nearestIntersection',
    'distance1',
    'distance2',
    'municipalityType',
    'crossStreet1',
    'crossStreet2',
    'status',
  ]),
};

function normalizePersonCfs(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'CFS',
    displayId: r.name,
    path: `/cfs/${r._id}?newTab=true`,
    location: getAddressLocation(r.address) || undefined,
  };
}

function normalizePersonArrest(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'ARREST',
    displayId: r.arrestId || r.number,
    path: r.kaseId ? `/cases/${r.kaseId}/arrests` : undefined,
  };
}

function normalizePersonCase(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'CASE',
    displayId: r.name,
    path: `/cases/${r._id}/overview`,
    location: getAddressLocation(r.address) || undefined,
  };
}

function normalizePersonWarrant(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'WARRANT',
    displayId: r.recordNumber,
    path: `/tools/wanted-person/view/${r._id}`,
  };
}

function normalizePersonTask(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'TASK',
    displayId: r.taskID,
    path: `/tasks/${r._id}`,
  };
}

function normalizePersonDir(r: any): MergeRelatedRecord {
  return {
    _id: String(r._id),
    type: 'DIR',
    displayId: r.dirId,
    path: r.kase ? `/cases/${r.kase}/overview` : undefined,
    location: getAddressLocation(r.address) || undefined,
  };
}

function normalizePersonSexOffender(r: any): MergeRelatedRecord {
  // A person has at most one sex-offender record; show the risk level (or
  // `Registered` when unset) as the row's identifier so the user can tell
  // it's the registration entry without needing a sex-offender number.
  const riskLevel = r?.riskLevel ? String(r.riskLevel).toUpperCase() : null;
  return {
    _id: String(r._id),
    type: 'SEX OFFENDER',
    displayId: riskLevel ? `Risk ${riskLevel}` : 'Registered',
    path: `/tools/sex-offenders/${r._id}`,
  };
}

function normalizeGenericRelated(r: any): MergeRelatedRecord {
  // Some backends label case records as `KASE` (internal model name); the
  // merge UI surfaces the user-facing `CASE` label uniformly.
  const rawType =
    typeof r.type === 'string' && r.type ? r.type.toUpperCase() : '';
  const type = rawType === 'KASE' ? 'CASE' : rawType || 'RECORD';
  return {
    _id: String(r._id),
    type,
    displayId: r.name,
    path: getRelatedRecordPath(r) || undefined,
    location: getAddressLocation(r.address) || r.location || undefined,
  };
}

async function fetchPersonRelated(id: string): Promise<MergeRelatedRecord[]> {
  const [cfs, arrests, cases, warrants, tasks, dir, sexOffenders] =
    await Promise.all([
      MasterIndexApi.getPersonRelatedCfs(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedArrests(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedCases(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedWarrants(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedTasks(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedDir(id, { limit: 200, page: 1 }),
      MasterIndexApi.getPersonRelatedSexOffenders(id, { limit: 200, page: 1 }),
    ]);
  const get = (r: any): any[] => r?.data?.docs ?? r?.data?.results ?? [];
  return [
    ...get(cfs).map(normalizePersonCfs),
    ...get(arrests).map(normalizePersonArrest),
    ...get(cases).map(normalizePersonCase),
    ...get(warrants).map(normalizePersonWarrant),
    ...get(tasks).map(normalizePersonTask),
    ...get(dir).map(normalizePersonDir),
    ...get(sexOffenders).map(normalizePersonSexOffender),
  ];
}

export type UseMergeToolReturn = {
  step: 'search' | 'conflict';
  currentRecord: AnyMasterRecord | null;
  currentLoading: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchResults: AnyMasterRecord[];
  searchLoading: boolean;
  selectedRecord: AnyMasterRecord | null;
  conflicts: MergeConflict[];
  choices: Record<string, FieldChoice>;
  setChoice: (key: string, side: FieldChoice) => void;
  selectAll: (side: FieldChoice) => void;
  deselectRecord: () => void;
  selectedLoading: boolean;
  selectRecord: (record: AnyMasterRecord) => void;
  onSearchFocus: () => void;
  showConfirmDialog: boolean;
  setShowConfirmDialog: (v: boolean) => void;
  sourceNotes: INote[];
  noteChecks: Record<string, boolean>;
  toggleNote: (id: string) => void;
  selectAllNotes: (checked: boolean) => void;
  sourceCautions: any[];
  cautionChecks: Record<string, boolean>;
  toggleCaution: (id: string) => void;
  selectAllCautions: (checked: boolean) => void;
  currentNotes: INote[];
  currentCautions: any[];
  currentFlags: IPersonFlag[];
  /**
   * Source MANUAL flags after excluding those that overlap with target's
   * (overlap is auto-resolved server-side — target keeps its flag, source's
   * is soft-deleted). Only these are shown as toggleable rows.
   */
  sourceFlags: IPersonFlag[];
  /**
   * SYSTEM-origin flags on each record (WP, etc.). Displayed read-only in the
   * conflict step so the user sees what will transfer automatically alongside
   * the toggleable MANUAL flags.
   */
  currentSystemFlags: IPersonFlag[];
  sourceSystemFlags: IPersonFlag[];
  flagChecks: Record<string, boolean>;
  toggleFlag: (id: string) => void;
  currentRelatedRecords: MergeRelatedRecord[];
  relatedRecords: MergeRelatedRecord[];
  /**
   * True when either record has `addressPrePlans` entries for agencies other
   * than the active one (addresses only). Drives the inline warning under
   * the Pre-Plan section that those entries won't be merged. Always false
   * for non-address types.
   */
  prePlanHasOtherAgencyData: boolean;
  executeMerge: () => Promise<void>;
  merging: boolean;
};

export function useMergeTool(
  masterIndexType: MasterIndexType,
  currentEntityId: string | undefined,
  open: boolean,
  onMergeComplete: () => void,
): UseMergeToolReturn {
  const { updateToast } = useToastStore();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestQuery = useRef('');

  const [step, setStep] = useState<'search' | 'conflict'>('search');
  const [currentRecord, setCurrentRecord] = useState<AnyMasterRecord | null>(
    null,
  );
  const [currentLoading, setCurrentLoading] = useState(false);
  const [searchQuery, setSearchQueryState] = useState('');
  const [searchResults, setSearchResults] = useState<AnyMasterRecord[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AnyMasterRecord | null>(
    null,
  );
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [conflicts, setConflicts] = useState<MergeConflict[]>([]);
  const [choices, setChoices] = useState<Record<string, FieldChoice>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sourceNotes, setSourceNotes] = useState<INote[]>([]);
  const [noteChecks, setNoteChecks] = useState<Record<string, boolean>>({});
  const [sourceCautions, setSourceCautions] = useState<any[]>([]);
  const [cautionChecks, setCautionChecks] = useState<Record<string, boolean>>(
    {},
  );
  const [currentNotes, setCurrentNotes] = useState<INote[]>([]);
  const [currentCautions, setCurrentCautions] = useState<any[]>([]);
  const [currentFlags, setCurrentFlags] = useState<IPersonFlag[]>([]);
  const [sourceFlags, setSourceFlags] = useState<IPersonFlag[]>([]);
  /**
   * SYSTEM-origin flags (e.g. WP — Wanted Person) on each record. The merge
   * doesn't expose them as toggleable rows; the BE registry rewriter
   * reassigns them automatically. They're surfaced here so the conflict UI
   * can display them as informational, disabled rows — same pattern as
   * related records.
   */
  const [currentSystemFlags, setCurrentSystemFlags] = useState<IPersonFlag[]>(
    [],
  );
  const [sourceSystemFlags, setSourceSystemFlags] = useState<IPersonFlag[]>([]);
  /** Per-source-flag bring-over opt-in. Default = false (opt in to bring over). */
  const [flagChecks, setFlagChecks] = useState<Record<string, boolean>>({});
  const [currentRelatedRecords, setCurrentRelatedRecords] = useState<
    MergeRelatedRecord[]
  >([]);
  const [relatedRecords, setRelatedRecords] = useState<MergeRelatedRecord[]>(
    [],
  );
  const [merging, setMerging] = useState(false);
  /**
   * True when EITHER address (current or matched) carries `addressPrePlans`
   * entries for agencies other than the active one. Used to surface an
   * inline warning in the Pre-Plan section: those entries won't be merged
   * (target's are preserved on the surviving doc; source's stay on the
   * soft-deleted tombstone). Sourced from the address GET response's
   * `hasOtherAgencyPrePlans` flag.
   */
  const [prePlanHasOtherAgencyData, setPrePlanHasOtherAgencyData] =
    useState(false);
  const { userAgencyId } = useUserStore();

  // Fetch current record when panel opens
  useEffect(() => {
    if (!open || !currentEntityId) return;
    let cancelled = false;

    const fetchCurrent = async () => {
      setCurrentLoading(true);
      try {
        let data: AnyMasterRecord | null = null;
        switch (masterIndexType) {
          case 'persons': {
            const res = await MasterNameApi.getMasterName(currentEntityId);
            data = res.data;
            try {
              const ssnRes = await MasterNameApi.getMasterNameSSN(
                currentEntityId,
              );
              if (ssnRes.data?.ssn) (data as any).ssn = ssnRes.data.ssn;
            } catch {
              // SSN fetch is best-effort
            }
            break;
          }
          case 'address': {
            const res = await MasterIndexApi.getMasterAddress(currentEntityId);
            data = res.data;
            break;
          }
          case 'organizations': {
            const res = await MasterIndexApi.getMasterOrganization(
              currentEntityId,
            );
            data = res.data;
            break;
          }
          case 'vehicles': {
            const res = await MasterIndexApi.getMasterVehicle(currentEntityId);
            data = res.data;
            break;
          }
        }
        if (!cancelled) setCurrentRecord(data);
        if (!cancelled && masterIndexType === 'address') {
          setPrePlanHasOtherAgencyData(
            Boolean((data as any)?.hasOtherAgencyPrePlans),
          );
        }

        // Fetch current record's notes / cautions for read-only display
        // alongside the matched record's notes in the merge UI. Same endpoints
        // the detail page uses.
        let cNotes: INote[] = [];
        let cCautions: any[] = [];
        try {
          switch (masterIndexType) {
            case 'persons':
            case 'organizations': {
              const r = await MasterNameApi.getPaginatedMasterNameNotes(
                currentEntityId,
                { limit: 200, page: 1 },
              );
              cNotes = (r as any).data?.docs ?? (r as any).data?.results ?? [];
              break;
            }
            case 'vehicles': {
              const r = await MasterVehicleApi.getPaginatedMasterVehicleNotes(
                currentEntityId,
                { limit: 200, page: 1 },
              );
              cNotes = (r as any).data?.docs ?? (r as any).data?.results ?? [];
              break;
            }
            case 'address': {
              const addressFilter = JSON.stringify({
                items: [
                  {
                    field: 'address',
                    operator: 'equals',
                    value: currentEntityId,
                  },
                ],
              });
              const [notesRes, cautionsRes] = await Promise.all([
                AddressNoteApi.getPaginatedAddressNotes({
                  filter: addressFilter,
                  limit: 200,
                  page: 1,
                }),
                CautionApi.getPaginatedCautions({
                  filter: addressFilter,
                  limit: 200,
                  page: 1,
                }),
              ]);
              cNotes = ((notesRes as any).data?.docs ??
                (notesRes as any).data?.results ??
                []) as INote[];
              cCautions =
                (cautionsRes as any).data?.docs ??
                (cautionsRes as any).data?.results ??
                [];
              break;
            }
          }
        } catch {
          // Best-effort; absence of notes is informational only.
        }
        if (!cancelled) {
          setCurrentNotes(cNotes);
          setCurrentCautions(cCautions);
          // Seed current-side checks to true ("keep on merged record"). The
          // user flips an entry to false to drop the note/caution. Source
          // entries are added later in `selectRecord` with default false.
          setNoteChecks((prev) => ({
            ...prev,
            ...Object.fromEntries(
              cNotes.map((n: any) => [String(n._id), true]),
            ),
          }));
          setCautionChecks((prev) => ({
            ...prev,
            ...Object.fromEntries(
              cCautions.map((c: any) => [String(c._id), true]),
            ),
          }));
        }

        // Fetch current person's flags for the per-flag merge UI. MANUAL
        // flags get user-toggleable rows (keep/drop); SYSTEM flags (WP, etc.)
        // are auto-handled server-side via the registry rewriter — they're
        // surfaced here as informational, disabled rows so the user can see
        // what will transfer alongside the manual ones.
        let cFlags: IPersonFlag[] = [];
        let cSystemFlags: IPersonFlag[] = [];
        if (masterIndexType === 'persons') {
          try {
            const fr = await FlagsApi.getPersonFlags(currentEntityId);
            const active = (fr.data ?? []).filter((f) => !f.isDeleted);
            cFlags = active.filter((f) => f.origin === FlagOrigin.MANUAL);
            cSystemFlags = active.filter((f) => f.origin !== FlagOrigin.MANUAL);
          } catch {
            // Best-effort; absence of flags is informational only.
          }
        }
        if (!cancelled) {
          setCurrentFlags(cFlags);
          setCurrentSystemFlags(cSystemFlags);
          // Default current flags to "keep". User can flip an entry to drop.
          setFlagChecks((prev) => ({
            ...prev,
            ...Object.fromEntries(cFlags.map((f) => [String(f._id), true])),
          }));
        }

        // Fetch current record's related records for read-only display
        // alongside the matched record's related records. Mirrors the
        // matched-record fetch in `selectRecord`.
        let cRelated: MergeRelatedRecord[] = [];
        try {
          switch (masterIndexType) {
            case 'persons': {
              cRelated = await fetchPersonRelated(currentEntityId);
              break;
            }
            case 'organizations':
            case 'vehicles': {
              const apiPath =
                masterIndexType === 'organizations'
                  ? MasterIndexApiPath.Organizations
                  : MasterIndexApiPath.Vehicles;
              const r = await MasterIndexApi.getMasterRelatedRecords(
                apiPath,
                currentEntityId,
                {
                  sort: JSON.stringify({ createdAt: -1 }),
                  limit: 200,
                  page: 1,
                },
              );
              const docs = (r as any).data?.docs ?? [];
              cRelated = (docs as any[]).map(normalizeGenericRelated);
              break;
            }
            case 'address': {
              const r = await MasterIndexApi.getAddressRelatedRecords(
                currentEntityId,
              );
              const d = (r as any).data;
              const arr = Array.isArray(d) ? d : d?.docs ?? d?.results ?? [];
              cRelated = (arr as any[]).map(normalizeGenericRelated);
              break;
            }
          }
        } catch {
          // Best-effort; absence is informational only.
        }
        if (!cancelled) {
          setCurrentRelatedRecords(cRelated);
        }
      } finally {
        if (!cancelled) setCurrentLoading(false);
      }
    };

    fetchCurrent();
    return () => {
      cancelled = true;
    };
  }, [open, currentEntityId, masterIndexType]);

  // Reset all state when panel closes
  useEffect(() => {
    if (!open) {
      setStep('search');
      setSearchQueryState('');
      setSearchResults([]);
      setSelectedRecord(null);
      setSelectedLoading(false);
      setConflicts([]);
      setChoices({});
      setShowConfirmDialog(false);
      setCurrentRecord(null);
      setSourceNotes([]);
      setNoteChecks({});
      setSourceCautions([]);
      setCautionChecks({});
      setCurrentNotes([]);
      setCurrentCautions([]);
      setCurrentFlags([]);
      setSourceFlags([]);
      setCurrentSystemFlags([]);
      setSourceSystemFlags([]);
      setFlagChecks({});
      setCurrentRelatedRecords([]);
      setRelatedRecords([]);
      setPrePlanHasOtherAgencyData(false);
      latestQuery.current = '';
    }
  }, [open]);

  const doSearch = useCallback(
    async (query: string) => {
      setSearchLoading(true);
      try {
        let results: AnyMasterRecord[] = [];
        // Mirror the master-index DataGrid's behavior: tokenize on whitespace
        // so multi-word queries (e.g. "123 Main St") become separate
        // quickFilterValues entries. The BE address search builder treats
        // each entry as a required token, so sending the whole query as one
        // element returned a different (narrower) result set than the
        // master-index Addresses list does for the same input.
        const tokens = query.trim().split(/\s+/).filter(Boolean);
        const params = tokens.length
          ? {
              filter: JSON.stringify({
                items: [],
                logicOperator: 'and',
                quickFilterValues: tokens,
                quickFilterLogicOperator: 'and',
              }),
              limit: 20,
              page: 1,
            }
          : { limit: 20, page: 1 };

        switch (masterIndexType) {
          case 'persons': {
            const res = await MasterIndexApi.getMasterPersons(params);
            results = res.data.docs ?? [];
            break;
          }
          case 'address': {
            const res = await MasterIndexApi.getMasterAddresses(params);
            results = (res.data as any).results ?? (res.data as any).docs ?? [];
            if (!Array.isArray(results)) results = [];
            break;
          }
          case 'organizations': {
            const res = await MasterIndexApi.getMasterOrganizations(params);
            results = res.data.docs ?? [];
            break;
          }
          case 'vehicles': {
            const res = await MasterIndexApi.getMasterVehicles(params);
            results = res.data.docs ?? [];
            break;
          }
        }

        setSearchResults(
          results.filter((r) => (r as any)._id !== currentEntityId),
        );
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [masterIndexType, currentEntityId],
  );

  const onSearchFocus = useCallback(() => {
    if (searchResults.length === 0 && !searchQuery.trim()) doSearch('');
  }, [searchResults.length, searchQuery, doSearch]);

  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);
      latestQuery.current = query;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (latestQuery.current === query) doSearch(query);
      }, 300);
    },
    [doSearch],
  );

  const selectRecord = useCallback(
    async (record: AnyMasterRecord) => {
      if (!currentRecord) return;
      setSelectedLoading(true);
      try {
        let fullRecord: AnyMasterRecord = record;
        const id = (record as any)._id ?? (record as any).id;
        switch (masterIndexType) {
          case 'persons': {
            const res = await MasterNameApi.getMasterName(id);
            fullRecord = res.data;
            try {
              const ssnRes = await MasterNameApi.getMasterNameSSN(id);
              if (ssnRes.data?.ssn) (fullRecord as any).ssn = ssnRes.data.ssn;
            } catch {
              // SSN fetch is best-effort
            }
            break;
          }
          case 'address': {
            const res = await MasterIndexApi.getMasterAddress(id);
            fullRecord = res.data;
            break;
          }
          case 'organizations': {
            const res = await MasterIndexApi.getMasterOrganization(id);
            fullRecord = res.data;
            break;
          }
          case 'vehicles': {
            const res = await MasterIndexApi.getMasterVehicle(id);
            fullRecord = res.data;
            break;
          }
        }

        // Fetch additive sub-resources (notes, cautions) from the matched
        // record so the user can pick which ones to bring over. Default =
        // bring all. Each fetch is independent so a failure in one doesn't
        // hide the others.
        const fullRecordId = (fullRecord as any)._id ?? (fullRecord as any).id;

        let notes: INote[] = [];
        let cautions: any[] = [];
        try {
          switch (masterIndexType) {
            case 'persons':
            case 'organizations': {
              const r = await MasterNameApi.getPaginatedMasterNameNotes(
                fullRecordId,
                { limit: 200, page: 1 },
              );
              notes = (r as any).data?.docs ?? (r as any).data?.results ?? [];
              break;
            }
            case 'vehicles': {
              const r = await MasterVehicleApi.getPaginatedMasterVehicleNotes(
                fullRecordId,
                { limit: 200, page: 1 },
              );
              notes = (r as any).data?.docs ?? (r as any).data?.results ?? [];
              break;
            }
            case 'address': {
              // Address notes live in their own collection - fetch by address id
              // (same query the address detail page uses).
              const addressFilter = JSON.stringify({
                items: [
                  {
                    field: 'address',
                    operator: 'equals',
                    value: fullRecordId,
                  },
                ],
              });
              const [notesRes, cautionsRes] = await Promise.all([
                AddressNoteApi.getPaginatedAddressNotes({
                  filter: addressFilter,
                  limit: 200,
                  page: 1,
                }),
                CautionApi.getPaginatedCautions({
                  filter: addressFilter,
                  limit: 200,
                  page: 1,
                }),
              ]);
              notes = ((notesRes as any).data?.docs ??
                (notesRes as any).data?.results ??
                []) as INote[];
              cautions =
                (cautionsRes as any).data?.docs ??
                (cautionsRes as any).data?.results ??
                [];
              break;
            }
          }
        } catch (err: any) {
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: `Failed to load notes: ${
              err?.response?.data?.message ?? err?.message ?? 'unknown error'
            }`,
          });
        }
        setSourceNotes(notes);
        // Seed source-side entries to false (don't bring over by default).
        // Merge with existing entries so current-side seeds aren't wiped.
        setNoteChecks((prev) => ({
          ...prev,
          ...Object.fromEntries(notes.map((n: any) => [String(n._id), false])),
        }));
        setSourceCautions(cautions);
        setCautionChecks((prev) => ({
          ...prev,
          ...Object.fromEntries(
            cautions.map((c: any) => [String(c._id), false]),
          ),
        }));

        // Fetch source flags. MANUAL flags drive the toggleable per-flag UI;
        // SYSTEM flags (WP, etc.) are auto-reassigned server-side via the
        // registry rewriter and render as informational, disabled rows.
        // Overlap between source and target MANUAL flags is *not* filtered
        // here — the conflict step still shows those rows so the user sees
        // what's on both records. Overlap is auto-resolved server-side
        // (target keeps its copy, source's gets soft-deleted). Per-flag
        // opt-in only applies to source-only MANUAL flags; the FE filters
        // `sourceFlagIds` accordingly at submit time.
        let allSourceManual: IPersonFlag[] = [];
        let allSourceSystem: IPersonFlag[] = [];
        if (masterIndexType === 'persons') {
          try {
            const fr = await FlagsApi.getPersonFlags(fullRecordId);
            const active = (fr.data ?? []).filter((f) => !f.isDeleted);
            allSourceManual = active.filter(
              (f) => f.origin === FlagOrigin.MANUAL,
            );
            allSourceSystem = active.filter(
              (f) => f.origin !== FlagOrigin.MANUAL,
            );
          } catch {
            // Best-effort; absence of flags is informational only.
          }
        }
        setSourceFlags(allSourceManual);
        setSourceSystemFlags(allSourceSystem);
        // Seed source-side flag entries to false (don't bring over). Merge
        // with the existing map so the current-side seeds (= true, "keep")
        // aren't overwritten.
        setFlagChecks((prev) => ({
          ...prev,
          ...Object.fromEntries(
            allSourceManual.map((f) => [String(f._id), false]),
          ),
        }));

        // Fetch related records that will be reassigned on merge. Each type
        // has its own endpoint shape:
        // - persons: fetch per-category records in parallel and normalize
        // - orgs / vehicles: paginated list via getMasterRelatedRecords
        // - addresses: dedicated getAddressRelatedRecords (returns a flat array)
        let related: MergeRelatedRecord[] = [];
        try {
          switch (masterIndexType) {
            case 'persons': {
              related = await fetchPersonRelated(fullRecordId);
              break;
            }
            case 'organizations':
            case 'vehicles': {
              const apiPath =
                masterIndexType === 'organizations'
                  ? MasterIndexApiPath.Organizations
                  : MasterIndexApiPath.Vehicles;
              const r = await MasterIndexApi.getMasterRelatedRecords(
                apiPath,
                fullRecordId,
                {
                  sort: JSON.stringify({ createdAt: -1 }),
                  limit: 200,
                  page: 1,
                },
              );
              const docs = (r as any).data?.docs ?? [];
              related = (docs as any[]).map(normalizeGenericRelated);
              break;
            }
            case 'address': {
              const r = await MasterIndexApi.getAddressRelatedRecords(
                fullRecordId,
              );
              const d = (r as any).data;
              const arr = Array.isArray(d) ? d : d?.docs ?? d?.results ?? [];
              related = (arr as any[]).map(normalizeGenericRelated);
              break;
            }
          }
        } catch (err: any) {
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: `Failed to load related records: ${
              err?.response?.data?.message ?? err?.message ?? 'unknown error'
            }`,
          });
        }
        setRelatedRecords(related);

        const computed = getConflicts(
          masterIndexType,
          currentRecord,
          fullRecord,
        );

        // Pre-plan conflict synthesis (addresses only). The standard field
        // config drives a flat per-key comparison and doesn't know about the
        // per-agency embedded `addressPrePlans` array. We synthesize one
        // conflict row per differing MDF subfield from the current-agency
        // entry on each side, using `getFormFields()` for labels/types
        // (same source the existing pre-plan view uses, so the labels stay
        // in sync without a hardcoded list).
        //
        // The standard `setChoice`/`renderRadioCell` machinery in
        // MergeConflictStep handles the rest — preplan rows render under a
        // `Pre-Plan` section header alongside notes / cautions / flags.
        if (masterIndexType === 'address' && userAgencyId) {
          const currentPlan = pickCurrentAgencyPrePlan(
            currentRecord,
            userAgencyId,
          );
          const matchedPlan = pickCurrentAgencyPrePlan(
            fullRecord,
            userAgencyId,
          );
          if (currentPlan || matchedPlan) {
            const option =
              ((currentPlan?.prePlanOption ?? matchedPlan?.prePlanOption) as
                | string
                | undefined) ?? 'MDF';
            const formFields = (getFormFields() ?? {}) as Record<
              string,
              PrePlanFormField[]
            >;
            const fields = formFields[option] ?? [];
            for (const f of fields) {
              const subKey = String(f.name).split('.').pop() ?? '';
              if (!subKey || PREPLAN_META_KEYS.has(subKey)) continue;
              const cv = currentPlan?.[subKey];
              const sv = matchedPlan?.[subKey];
              if (isEqual(cv, sv)) continue;
              if (isValueEmpty(cv) && isValueEmpty(sv)) continue;
              computed.push({
                field: {
                  key: `${PREPLAN_CONFLICT_PREFIX}${subKey}`,
                  label: f.label,
                  section: 'Pre-Plan',
                  // File subfields piggy-back on the existing photo renderer,
                  // which already handles arbitrary file types via FileGrid /
                  // FileCellValue. Scalars get the per-form-field formatter
                  // so dates / booleans / select options render the same way
                  // the read-only pre-plan modal does.
                  ...(f.type === 'file'
                    ? { renderType: 'photo' as const }
                    : { formatter: makePrePlanFormatter(f) }),
                },
                currentValue: cv,
                selectedValue: sv,
              });
            }
          }
          // Combine matched-record flag with the current-record flag we
          // already captured. Either side having other-agency entries
          // triggers the warning.
          setPrePlanHasOtherAgencyData(
            (prev) =>
              prev || Boolean((fullRecord as any)?.hasOtherAgencyPrePlans),
          );
        }

        const initial: Record<string, FieldChoice> = {};
        computed.forEach(({ field }) => {
          initial[field.key] = 'current';
        });
        setSelectedRecord(fullRecord);
        setConflicts(computed);
        setChoices(initial);
        setStep('conflict');
      } catch {
        // Fall back to partial record if detail fetch fails
        const computed = getConflicts(masterIndexType, currentRecord, record);
        const initial: Record<string, FieldChoice> = {};
        computed.forEach(({ field }) => {
          initial[field.key] = 'current';
        });
        setSelectedRecord(record);
        setConflicts(computed);
        setChoices(initial);
        setStep('conflict');
      } finally {
        setSelectedLoading(false);
      }
    },
    [currentRecord, masterIndexType, currentFlags, userAgencyId],
  );

  const deselectRecord = useCallback(() => {
    setSelectedRecord(null);
    setConflicts([]);
    setChoices({});
    setSourceNotes([]);
    setNoteChecks({});
    setSourceCautions([]);
    setCautionChecks({});
    setSourceFlags([]);
    setSourceSystemFlags([]);
    setFlagChecks({});
    setRelatedRecords([]);
    // Re-derive the warning from currentRecord only — selectRecord may have
    // OR'd in matched's flag, which no longer applies once a matched record
    // is deselected.
    setPrePlanHasOtherAgencyData(
      Boolean((currentRecord as any)?.hasOtherAgencyPrePlans),
    );
    setStep('search');
  }, [currentRecord]);

  const setChoice = useCallback((key: string, side: FieldChoice) => {
    setChoices((prev) => ({ ...prev, [key]: side }));
  }, []);

  const selectAll = useCallback((side: FieldChoice) => {
    setChoices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = side;
      });
      return next;
    });
  }, []);

  const toggleNote = useCallback((id: string) => {
    setNoteChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const selectAllNotes = useCallback((checked: boolean) => {
    setNoteChecks((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        next[k] = checked;
      });
      return next;
    });
  }, []);

  const toggleCaution = useCallback((id: string) => {
    setCautionChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleFlag = useCallback((id: string) => {
    setFlagChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const selectAllCautions = useCallback((checked: boolean) => {
    setCautionChecks((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        next[k] = checked;
      });
      return next;
    });
  }, []);

  const executeMerge = useCallback(async () => {
    if (!currentRecord || !selectedRecord || !currentEntityId) return;
    setMerging(true);
    try {
      // Build mergedFields - the resolved winners - using the same pass 1 /
      // pass 2 logic as before, but without per-type sub-resource side
      // effects. The backend whitelists fields per type, reassigns
      // references, and transfers sub-resources atomically.
      const mergedFields: Record<string, unknown> = {};
      Object.assign(
        mergedFields,
        currentRecord as unknown as Record<string, unknown>,
      );

      // Top-level keys the user explicitly chose to clear (selected-side value
      // is null/undefined). `JSON.stringify` strips undefined values, so the
      // backend can't see the choice via `mergedFields` alone — we ship them
      // as an explicit `unsetFields` list and the backend applies `$unset`.
      // Dotted keys (e.g. `physicalCharacteristics.weight`) don't need this
      // path: the parent object gets `$set` wholesale and the absent sub-key
      // is cleared as a side effect.
      const unsetFields = new Set<string>();

      // Pass 1: scalar + virtual choices.
      conflicts.forEach(({ field, parentKey }) => {
        if (parentKey !== undefined) return;
        // Synthesized preplan rows (`__prePlan__<subkey>`) are resolved in
        // the address case below — they're not record-level scalar fields,
        // so they must not feed `mergedFields` / `unsetFields` (the BE
        // rejects unknown unset keys).
        if (field.key.startsWith(PREPLAN_CONFLICT_PREFIX)) return;
        if (choices[field.key] === 'selected') {
          if (field.virtual) {
            field.virtual.keys.forEach((k) => {
              const v = getNestedValue(selectedRecord, k);
              setNestedValue(mergedFields, k, v);
              if (!k.includes('.') && v == null) {
                unsetFields.add(k);
                delete (mergedFields as any)[k];
              }
            });
          } else {
            const v = getNestedValue(selectedRecord, field.key);
            setNestedValue(mergedFields, field.key, v);
            if (!field.key.includes('.') && v == null) {
              unsetFields.add(field.key);
              delete (mergedFields as any)[field.key];
            }
          }
        }
      });

      // Pass 2: expand-array reconstruction.
      const expandedByParent = new Map<string, typeof conflicts>();
      conflicts.forEach((c) => {
        if (c.parentKey !== undefined) {
          const bucket = expandedByParent.get(c.parentKey) ?? [];
          bucket.push(c);
          expandedByParent.set(c.parentKey, bucket);
        }
      });
      expandedByParent.forEach((itemConflicts, parentKey) => {
        const cArr = getNestedValue(currentRecord, parentKey);
        const sArr = getNestedValue(selectedRecord, parentKey);
        const currentItems = Array.isArray(cArr) ? cArr : [];
        const selectedItems = Array.isArray(sArr) ? sArr : [];
        const maxLen = Math.max(currentItems.length, selectedItems.length);
        const conflictIndexMap = new Map(
          itemConflicts.map((c) => [c.arrayIndex ?? 0, c.field.key]),
        );
        const result: unknown[] = [];
        for (let i = 0; i < maxLen; i++) {
          const conflictKey = conflictIndexMap.get(i);
          if (conflictKey !== undefined) {
            const side = choices[conflictKey] ?? 'current';
            const item =
              side === 'selected' ? selectedItems[i] : currentItems[i];
            if (item !== undefined) result.push(item);
          } else if (i < currentItems.length) {
            result.push(currentItems[i]);
          }
        }
        setNestedValue(mergedFields, parentKey, result);
      });

      // Filter mergedFields through the per-type writable whitelist. Anything
      // outside the set (audit fields, virtuals like `name`/`isWanted`, denorm
      // counts, sub-resource arrays handled via dedicated payload fields, etc.)
      // is dropped — the backend rejects unknown keys with 400.
      const writable = WRITABLE_FIELDS_BY_TYPE[masterIndexType];
      for (const k of Object.keys(mergedFields)) {
        if (!writable.has(k)) {
          delete (mergedFields as any)[k];
        }
      }

      // Nested ref fields (address, employerInfo.address, vehicle color/owner)
      // arrive populated from the GET responses but the backend whitelist
      // expects bare ObjectId strings. Coerce before POST.
      //
      // `driverLicense` is deliberately *not* coerced here. Unlike the other
      // refs, the merge tool exposes per-subfield conflict rows for the DL
      // (state / number / vehicleClass / …) and the BE needs to read those
      // subfield values off the populated object to apply them to the
      // referenced DriverLicense document. The BE normalizes it down to the
      // bare id itself after extracting the subfield diff, so the wire
      // payload can safely carry the populated object.
      const toRef = (v: unknown): unknown => {
        if (v == null) return v;
        if (typeof v === 'string') return v;
        if (typeof v === 'object') {
          const id = (v as any)._id ?? (v as any).id;
          return id ?? v; // fall back to original object if no id field
        }
        return v;
      };

      switch (masterIndexType) {
        case 'persons': {
          if ('address' in mergedFields) {
            (mergedFields as any).address = toRef(
              (mergedFields as any).address,
            );
          }
          if (
            'employerInfo' in mergedFields &&
            (mergedFields as any).employerInfo
          ) {
            const ei = (mergedFields as any).employerInfo;
            (mergedFields as any).employerInfo = {
              ...ei,
              address: toRef(ei.address),
            };
          }
          break;
        }
        case 'organizations': {
          if ('address' in mergedFields) {
            (mergedFields as any).address = toRef(
              (mergedFields as any).address,
            );
          }
          break;
        }
        case 'vehicles': {
          if ('color' in mergedFields) {
            (mergedFields as any).color = toRef((mergedFields as any).color);
          }
          if ('owner' in mergedFields) {
            (mergedFields as any).owner = toRef((mergedFields as any).owner);
          }
          break;
        }
        case 'address':
          break;
      }

      // The matched (selected) record is the one being merged away. The
      // current record (currentEntityId) is the one being kept.
      const sourceId =
        (selectedRecord as any)._id ?? (selectedRecord as any).id;
      const targetId = currentEntityId;

      // Notes/cautions live in the unified check map keyed by note id. Each
      // side gets a different default (current = true / keep, source = false
      // / don't bring), so partition by which list a note actually belongs to.
      const sourceNoteIdSet = new Set(
        sourceNotes.map((n: any) => String(n._id)),
      );
      const noteSourceIds = sourceNotes
        .filter((n: any) => noteChecks[String(n._id)] === true)
        .map((n: any) => String(n._id));
      const removeNoteIds = currentNotes
        .filter(
          (n: any) =>
            !sourceNoteIdSet.has(String(n._id)) &&
            noteChecks[String(n._id)] === false,
        )
        .map((n: any) => String(n._id));

      const sourceCautionIdSet = new Set(
        sourceCautions.map((c: any) => String(c._id)),
      );
      const cautionSourceIds = sourceCautions
        .filter((c: any) => cautionChecks[String(c._id)] === true)
        .map((c: any) => String(c._id));
      const removeCautionIds = currentCautions
        .filter(
          (c: any) =>
            !sourceCautionIdSet.has(String(c._id)) &&
            cautionChecks[String(c._id)] === false,
        )
        .map((c: any) => String(c._id));
      // Flags: opt-in applies to source-only flags (default false → true to
      // bring over); removal applies to current-only flags (default true →
      // false to drop from target). Overlap flags aren't surfaced in the UI
      // — the backend handles them automatically.
      const flagRefOf = (f: any) => String(f.flag?._id ?? f.flag);
      const targetFlagRefs = new Set(currentFlags.map(flagRefOf));
      const sourceFlagRefs = new Set(sourceFlags.map(flagRefOf));
      const sourceFlagIds = sourceFlags
        .filter((f) => !targetFlagRefs.has(flagRefOf(f)))
        .filter((f) => flagChecks[String(f._id)] === true)
        .map((f) => f._id);
      const removeFlagIds = currentFlags
        .filter((f) => !sourceFlagRefs.has(flagRefOf(f)))
        .filter((f) => flagChecks[String(f._id)] === false)
        .map((f) => f._id);
      // Related-record opt-out was removed from the UI — all related records
      // are reassigned to the target. The BE still accepts a `skipIds` list,
      // we just always pass it empty.
      const skipRelatedIds: string[] = [];

      const unsetFieldList = Array.from(unsetFields);

      let reassigned: Record<string, number> = {};
      switch (masterIndexType) {
        case 'persons': {
          const r = await MasterIndexApi.mergeMasterPerson(sourceId, {
            targetId,
            mergedFields,
            unsetFields: unsetFieldList,
            notes: {
              sourceNoteIds: noteSourceIds,
              removeNoteIds,
            },
            flags: { sourceFlagIds, removeFlagIds },
            relatedRecords: { skipIds: skipRelatedIds },
          });
          reassigned = r.data.reassigned;
          break;
        }
        case 'organizations': {
          const r = await MasterIndexApi.mergeMasterOrganization(sourceId, {
            targetId,
            mergedFields,
            unsetFields: unsetFieldList,
            notes: {
              sourceNoteIds: noteSourceIds,
              removeNoteIds,
            },
            relatedRecords: { skipIds: skipRelatedIds },
          });
          reassigned = r.data.reassigned;
          break;
        }
        case 'vehicles': {
          const r = await MasterIndexApi.mergeMasterVehicle(sourceId, {
            targetId,
            mergedFields,
            unsetFields: unsetFieldList,
            notes: {
              sourceNoteIds: noteSourceIds,
              removeNoteIds,
            },
            relatedRecords: { skipIds: skipRelatedIds },
          });
          reassigned = r.data.reassigned;
          break;
        }
        case 'address': {
          // Build the pre-plan resolution for the current agency. The
          // synthesized `__prePlan__<key>` conflicts in `choices` capture
          // the user's per-subfield picks; subfields with no conflict
          // (equal on both sides) are filled in from whichever side has
          // a value. The resulting entry's `agencyId` / `prePlanOption`
          // are filled server-side from the request agency.
          let prePlans: { entry: Record<string, unknown> | null } | undefined;
          if (userAgencyId) {
            const currentPlan = pickCurrentAgencyPrePlan(
              currentRecord,
              userAgencyId,
            );
            const matchedPlan = pickCurrentAgencyPrePlan(
              selectedRecord,
              userAgencyId,
            );
            if (currentPlan || matchedPlan) {
              const option =
                ((currentPlan?.prePlanOption ?? matchedPlan?.prePlanOption) as
                  | string
                  | undefined) ?? 'MDF';
              const formFields = (getFormFields() ?? {}) as Record<
                string,
                Array<{ name: string }>
              >;
              const fields = formFields[option] ?? [];
              const resolved: Record<string, unknown> = {};
              let anyValue = false;
              for (const f of fields) {
                const subKey = String(f.name).split('.').pop() ?? '';
                if (!subKey || PREPLAN_META_KEYS.has(subKey)) continue;
                const cv = currentPlan?.[subKey];
                const sv = matchedPlan?.[subKey];
                const conflictKey = `${PREPLAN_CONFLICT_PREFIX}${subKey}`;
                let chosen: unknown;
                if (conflictKey in choices) {
                  chosen = choices[conflictKey] === 'current' ? cv : sv;
                } else {
                  // No conflict — values were equal or both empty. Carry
                  // the value forward (current side wins arbitrarily; if
                  // current is empty, fall back to matched's).
                  chosen = !isValueEmpty(cv) ? cv : sv;
                }
                if (!isValueEmpty(chosen)) {
                  resolved[subKey] = chosen;
                  anyValue = true;
                }
              }
              if (anyValue) {
                prePlans = { entry: resolved };
              } else if (currentPlan) {
                // User picked away every subfield (or both sides were
                // empty across the board) — clear target's entry.
                prePlans = { entry: null };
              }
              // else: neither side had usable data → leave target alone.
            }
          }
          const r = await MasterIndexApi.mergeMasterAddress(sourceId, {
            targetId,
            mergedFields,
            unsetFields: unsetFieldList,
            notes: {
              sourceNoteIds: noteSourceIds,
              removeNoteIds,
            },
            cautions: {
              sourceCautionIds: cautionSourceIds,
              removeCautionIds,
            },
            relatedRecords: { skipIds: skipRelatedIds },
            ...(prePlans ? { prePlans } : {}),
          });
          reassigned = r.data.reassigned;
          break;
        }
      }

      const summary = Object.entries(reassigned)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${n} ${k}`)
        .join(', ');

      // eslint-disable-next-line no-console
      console.log(summary);

      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Records merged successfully',
      });
      onMergeComplete();
    } catch (err: any) {
      updateToast({
        open: true,
        type: ConfirmTypes.ERROR,
        message:
          err?.response?.data?.message ??
          err?.message ??
          'Failed to merge records',
      });
    } finally {
      setMerging(false);
      setShowConfirmDialog(false);
    }
  }, [
    currentRecord,
    selectedRecord,
    currentEntityId,
    masterIndexType,
    conflicts,
    choices,
    noteChecks,
    cautionChecks,
    flagChecks,
    currentNotes,
    sourceNotes,
    currentCautions,
    sourceCautions,
    currentFlags,
    sourceFlags,
    userAgencyId,
    updateToast,
    onMergeComplete,
  ]);

  return {
    step,
    currentRecord,
    currentLoading,
    searchQuery,
    setSearchQuery,
    onSearchFocus,
    searchResults,
    searchLoading,
    selectedRecord,
    conflicts,
    choices,
    setChoice,
    selectAll,
    deselectRecord,
    selectedLoading,
    selectRecord,
    showConfirmDialog,
    setShowConfirmDialog,
    sourceNotes,
    noteChecks,
    toggleNote,
    selectAllNotes,
    sourceCautions,
    cautionChecks,
    toggleCaution,
    selectAllCautions,
    currentNotes,
    currentCautions,
    currentFlags,
    sourceFlags,
    currentSystemFlags,
    sourceSystemFlags,
    flagChecks,
    toggleFlag,
    currentRelatedRecords,
    relatedRecords,
    prePlanHasOtherAgencyData,
    executeMerge,
    merging,
  };
}
